import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../components/LanguageSelector";
import {
    FileText,
    Clock,
    Calendar,
    DollarSign,
    Scale,
    Users,
    Flag,
    Globe,
    Upload,
    X,
    Languages,
    User,
    Shield,
    MessageSquare,
    Bell,
    ChevronDown,
    Home,
    Menu,
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import CurrencyDropdown from "../../components/ui/CurrencyDropdown";
import SharedFooter3 from "../../Footer/SharedFooter3";
import SharedHeader from "../../Headers/SharedHeader";
import Spinner from "../../modules/shared/components/Spinner";
import { createUserJobs, getTypeCategories, useAppDispatch, getAllCurrencies } from "../../redux";
import { TypeEnum, JobRequiredExperienceEnum, JobDurationTimelineEnum } from "../../modules";
import { useSelector } from "react-redux";
import { selectLoaderValue } from "../../redux/Slices/loadingSlice";
import { getAllLanguages } from "../../redux/Actions/LanguageAction";
import { uploadContractFile } from "../../redux/Slices/contractSlice";
import { GlobalService } from "../../modules";
import AIDescriptionGenerator from "../../components/ui/AIDescriptionGenerator";
import {
    detectUserCurrency,
    getCachedCurrencyPreference,
    setCachedCurrencyPreference,
    findCurrencyIdByCode as utilFindCurrencyIdByCode,
    getCurrencySymbol
} from "../../lib/currencyUtils";

interface LawyerRequestFormProps {
    formData?: any;
    onSubmit?: (data: any) => void;
    onBack?: () => void;
    isEditMode?: boolean;
    initialData?: any;
}

const LawyerRequestForm: React.FC<LawyerRequestFormProps> = ({
    formData: parentFormData,
    onSubmit,
    onBack,
    isEditMode = false,
    initialData
}) => {
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(1);
    const [customService, setCustomService] = useState("");
    const [showCustomInput, setShowCustomInput] = useState(false);
    const { user, currencies } = useSelector((state: any) => state.auth);
    const { typeCategories } = useSelector((state: any) => state.client);
    const { languages } = useSelector((state: any) => state.language);

    // File upload state
    const [uploadedFiles, setUploadedFiles] = useState<Array<{ id: string, name: string, url: string, type: string }>>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadingFileName, setUploadingFileName] = useState("");
    const [languageDisplayLimit, setLanguageDisplayLimit] = useState(6);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [touched, setTouched] = useState({
        contactName: false,
        contactEmail: false,
        contactPhone: false,
        contactWhatsapp: false,
        budgetAmount: false,
    });
    const dispatch = useAppDispatch();
    const isLoading = useSelector(selectLoaderValue);
    const [formData, setFormData] = useState({
        // Step 1: Basic Project Info
        requestType: "", // quote, hire, both, information
        projectTitle: "",
        expertise: [],
        location: {
            country: "",
            language: [],
        },

        // Step 2: Project Details
        projectScope: {
            description: "",
            timeline: "",
            budget: {
                type: "fixed", // or 'hourly'
                amount: "",
                currency: "",
            },
        },

        // Step 3: Requirements & Documents
        requirements: {
            experience: "",
            qualifications: [],
            languages: [],
            documents: [],
        },

        // Step 4: Contact Information
        contactName: "",
        contactEmail: "",
        contactPhone: "",
        canContactByWhatsapp: false,
        contactWhatsapp: "",

        // Step 5: Terms and Conditions
        acceptTerms: false,
    });

    // Selected currency state (separate from formData to prevent re-render issues)
    const [selectedCurrency, setSelectedCurrency] = useState<{id: string; name: string; code: string} | null>(null);
    const currencyDetectionRunRef = useRef(false);
    
    // Sync selectedCurrency with formData ONLY when it actually changes
    useEffect(() => {
        const currencyId = formData.projectScope.budget.currency;
        if (currencyId && currencies && currencies.length > 0) {
            // Only update if the ID is different from current selectedCurrency
            if (selectedCurrency?.id !== currencyId) {
                const currency = currencies.find((c: any) => c.value === currencyId);
                if (currency) {
                    setSelectedCurrency({
                        id: currencyId,
                        name: currency.label || t("lawyerForm.defaultCurrency"),
                        code: currency.initial || t("lawyerForm.defaultCurrency")
                    });
                }
            }
        }
    }, [formData.projectScope.budget.currency, currencies, selectedCurrency?.id]); // Add selectedCurrency.id to prevent warning

    // Handle currency selection
    const handleCurrencySelect = (currency: {id: string; name: string; code: string} | null) => {
        if (currency) {
            setSelectedCurrency(currency);
            setFormData({
                ...formData,
                projectScope: {
                    ...formData.projectScope,
                    budget: {
                        ...formData.projectScope.budget,
                        currency: currency.id,
                    },
                },
            });
        }
    };

    // Languages are now fetched from backend via Redux

    // Validation functions
    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhone = (phone: string) => {
        const phoneRegex = /^\+[1-9]\d{1,14}$/;
        return phoneRegex.test(phone);
    };

    const getErrors = () => {
        const errors: any = {};

        // Budget Amount validation
        if (!formData.projectScope.budget.amount || formData.projectScope.budget.amount.trim() === "") {
            errors.budgetAmount = "Budget amount is required";
        } else if (parseFloat(formData.projectScope.budget.amount) <= 0) {
            errors.budgetAmount = "Budget amount must be greater than 0";
        }

        if (!formData.contactName.trim()) {
            errors.contactName = t("lawyerForm.validation.contactNameRequired");
        }

        if (!formData.contactEmail.trim()) {
            errors.contactEmail = t("lawyerForm.validation.contactEmailRequired");
        } else if (!validateEmail(formData.contactEmail)) {
            errors.contactEmail = t("lawyerForm.validation.emailInvalid");
        }

        if (!formData.contactPhone.trim()) {
            errors.contactPhone = t("lawyerForm.validation.contactPhoneRequired");
        } else if (!validatePhone(formData.contactPhone)) {
            errors.contactPhone = t("lawyerForm.validation.phoneInvalid");
        }

        if (formData.canContactByWhatsapp && !formData.contactWhatsapp.trim()) {
            errors.contactWhatsapp = t("lawyerForm.validation.whatsappRequired");
        } else if (formData.canContactByWhatsapp && !validatePhone(formData.contactWhatsapp)) {
            errors.contactWhatsapp = t("lawyerForm.validation.whatsappInvalid");
        }

        return errors;
    };

    const errors = getErrors();

    // Field change handlers
    const handleFieldChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleFieldBlur = (field: string) => {
        setTouched(prev => ({
            ...prev,
            [field]: true,
        }));
    };

    // Step validation
    const isStepValid = step => {
        switch (step) {
            case 1: {
                // When used as component, requestType comes from parent
                const hasRequestType = onSubmit ? true : formData.requestType;
                return hasRequestType && formData.projectTitle && formData.projectTitle.trim().length >= 10;
            }
            case 2:
                return (
                    formData.projectScope.description && formData.projectScope.description.trim().length >= 15 &&
                    formData.projectScope.timeline && formData.projectScope.timeline.trim().length > 0 &&
                    formData.projectScope.budget.amount && formData.projectScope.budget.amount.trim().length > 0 &&
                    formData.projectScope.budget.currency && formData.projectScope.budget.currency.trim().length > 0
                );
            case 3:
                return formData.requirements.experience && formData.requirements.experience.trim().length > 0 && 
                       formData.requirements.languages.length > 0;
            case 4:
                return !errors.contactName && !errors.contactEmail && !errors.contactPhone && 
                       (!formData.canContactByWhatsapp || !errors.contactWhatsapp);
            case 5:
                return formData.acceptTerms;
            default:
                return false;
        }
    };

    // Navigation functions
    const nextStep = () => {
        if (currentStep < 5 && isStepValid(currentStep)) {
            setCurrentStep(currentStep + 1);
        }
    };

    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    // Handle form submission
    const handleSubmit = async () => {
        // Prevent submission while uploading files
        if (isUploading) {
            GlobalService.showToast("warning", t("toast.titles.warning"), "Please wait for file upload to complete before submitting.");
            return;
        }

        // Mark form as submitted to show all validation errors
        setIsSubmitted(true);

        // Validate all required fields
        const validationErrors = [];

        // Step 1 validations
        if (!onSubmit && !formData.requestType) {
            validationErrors.push("Request type is required");
        }
        if (!formData.projectTitle || formData.projectTitle.trim().length < 10) {
            validationErrors.push("Project title must be at least 10 characters");
        }

        // Step 2 validations
        if (!formData.projectScope.description || formData.projectScope.description.trim().length < 15) {
            validationErrors.push("Project description must be at least 15 characters");
        }
        if (!formData.projectScope.timeline || formData.projectScope.timeline.trim().length === 0) {
            validationErrors.push("Timeline is required");
        }
        if (!formData.projectScope.budget.amount || formData.projectScope.budget.amount.trim().length === 0) {
            validationErrors.push("Budget amount is required");
        } else if (parseFloat(formData.projectScope.budget.amount) <= 0) {
            validationErrors.push("Budget must be greater than 0");
        }
        if (!formData.projectScope.budget.currency || formData.projectScope.budget.currency.trim().length === 0) {
            validationErrors.push("Currency is required");
        }

        // Step 3 validations
        if (!formData.requirements.experience || formData.requirements.experience.trim().length === 0) {
            validationErrors.push("Experience level is required");
        }
        if (formData.requirements.languages.length === 0) {
            validationErrors.push("At least one language is required");
        }

        // Step 4 validations - Contact Information
        const contactErrors = getErrors();
        if (contactErrors.contactName) validationErrors.push(contactErrors.contactName);
        if (contactErrors.contactEmail) validationErrors.push(contactErrors.contactEmail);
        if (contactErrors.contactPhone) validationErrors.push(contactErrors.contactPhone);
        if (contactErrors.contactWhatsapp) validationErrors.push(contactErrors.contactWhatsapp);

        // Step 5 validations - Terms and Conditions
        if (!formData.acceptTerms) {
            validationErrors.push("You must accept the terms and conditions to continue");
        }

        // If there are any validation errors, show them and prevent submission
        if (validationErrors.length > 0) {
            GlobalService.showToast("error", "Validation Error", validationErrors[0]);
            // Scroll to top to show validation errors
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        try {
            // Map form data to backend structure
            const lawyerFormData = {
                // Basic job information
                title: formData.projectTitle,
                description: formData.projectScope.description,
                budget: parseFloat(formData.projectScope.budget.amount) || 0,
                currency: { id: formData.projectScope.budget.currency },
                budgetType: { id: formData.projectScope.budget.type === "fixed" ? 1 : 2 },

                // Timeline mapping
                durationTimeline: {
                    id: mapTimelineToEnum(formData.projectScope.timeline)
                },

                // Experience mapping
                requiredExperience: {
                    id: mapExperienceToEnum(formData.requirements.experience)
                },

                // Languages mapping
                languages: formData.requirements.languages.map(langId => ({
                    id: langId
                })),

                // Files
                files: formData.requirements.documents || [],

                // Contact information
                contactName: formData.contactName,
                contactEmail: formData.contactEmail,
                contactPhone: formData.contactPhone,
                canContactByWhatsapp: formData.canContactByWhatsapp,
                contactWhatsapp: formData.contactWhatsapp,

                // Additional fields that might be needed
                expertise: formData.expertise,
                location: formData.location,
            };

            // If onSubmit callback is provided, use it (for PostaJob integration)
            if (onSubmit) {
                onSubmit(lawyerFormData);
            } else {
                // Fallback to direct API call (standalone mode)
                const payload = {
                    userId: {
                        id: user?.id,
                    },
                    isQuote: formData.requestType === "quote" || formData.requestType === "both",
                    isEmergency: false,
                    categoryId: {
                        id: typeCategories[0]?.id,
                    },
                    postalCode: 67,
                    province: t("lawyerForm.defaultProvince"),
                    address: t("lawyerForm.defaultAddress"),
                    city: t("lawyerForm.defaultCity"),
                    budget: parseFloat(formData.projectScope.budget.amount) || 0,
                    description: formData.projectScope.description,
                    title: formData.projectTitle,
                    currency: { id: formData.projectScope.budget.currency },
                    budgetType: { id: formData.projectScope.budget.type === "fixed" ? 1 : 2 },
                    // Add the new fields
                    languages: lawyerFormData.languages,
                    requiredExperience: lawyerFormData.requiredExperience,
                    durationTimeline: lawyerFormData.durationTimeline,
                    // Contact information
                    contactName: formData.contactName,
                    contactEmail: formData.contactEmail,
                    contactPhone: formData.contactPhone,
                    canContactByWhatsapp: formData.canContactByWhatsapp,
                    contactWhatsapp: formData.contactWhatsapp,
                };

                await dispatch(createUserJobs({ data: payload }));
            }
        } catch (error) {
            console.error("Error submitting form:", error);
        }
    };

    // Helper functions to map form values to enum IDs
    const mapTimelineToEnum = (timeline: string): number => {
        switch (timeline) {
            case "less_than_1_month":
                return JobDurationTimelineEnum.Less_Than_One_Month;
            case "1_to_3_months":
                return JobDurationTimelineEnum.One_To_Three_Months;
            case "3_to_6_months":
                return JobDurationTimelineEnum.Three_To_Six_Months;
            case "more_than_6_months":
                return JobDurationTimelineEnum.More_Than_Six_Months;
            default:
                return JobDurationTimelineEnum.One_To_Three_Months;
        }
    };

    const mapExperienceToEnum = (experience: string): number => {
        switch (experience) {
            case "entry":
                return JobRequiredExperienceEnum.entry;
            case "intermediate":
                return JobRequiredExperienceEnum.intermediate;
            case "expert":
                return JobRequiredExperienceEnum.expert;
            case "specialist":
                return JobRequiredExperienceEnum.specialist;
            default:
                return JobRequiredExperienceEnum.intermediate;
        }
    };

    // Render different steps
    const renderStep = () => {
        return (
            <div className="space-y-6">

                <Card className="p-6">
                    <h2 className="text-xl font-semibold mb-4">{t("lawyerForm.projectTitle")} <span className="text-red-500">{t("lawyerForm.required")}</span></h2>
                    <input
                        type="text"
                        value={formData.projectTitle}
                        onChange={e => {
                            const value = e.target.value;
                            if (value.length <= 70) {
                                setFormData({
                                    ...formData,
                                    projectTitle: value,
                                });
                            }
                        }}
                        placeholder={t("lawyerForm.projectTitlePlaceholder")}
                        className={`w-full p-3 border rounded-lg ${formData.projectTitle.length > 0 && formData.projectTitle.length < 10 ? "border-red-500" : "border-gray-300"}`}
                        maxLength={70}
                    />
                    <div className="flex justify-between items-center mt-2">
                        <p className="text-sm text-gray-500">
                            {t("lawyerForm.titleDisplayNote")}
                        </p>
                        <div className="flex items-center gap-2">
                            <span className={`text-sm ${formData.projectTitle.length > 0 && formData.projectTitle.length < 10 ? "text-red-500" : "text-gray-400"}`}>
                                {formData.projectTitle.length}/70
                            </span>
                            {formData.projectTitle.length > 0 && formData.projectTitle.length < 10 && (
                                <span className="text-xs text-red-500">{t("lawyerForm.titleMinLength")}</span>
                            )}
                        </div>
                    </div>
                </Card>
                <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold">{t("lawyerForm.projectDescription")} <span className="text-red-500">{t("lawyerForm.required")}</span></h2>
                        <AIDescriptionGenerator
                            title={formData.projectTitle}
                            serviceType={t("lawyerForm.serviceType")}
                            requestType={formData.requestType}
                            onDescriptionGenerated={(description) => {
                                setFormData({
                                    ...formData,
                                    projectScope: {
                                        ...formData.projectScope,
                                        description: description,
                                    },
                                });
                            }}
                            disabled={isLoading}
                            className="text-sm"
                        />
                    </div>
                    <textarea
                        value={formData.projectScope.description}
                        onChange={e => {
                            const value = e.target.value;
                            if (value.length <= 600) {
                                setFormData({
                                    ...formData,
                                    projectScope: {
                                        ...formData.projectScope,
                                        description: value,
                                    },
                                });
                            }
                        }}
                        rows={6}
                        className={`w-full p-3 border rounded-lg ${formData.projectScope.description.length > 0 && (formData.projectScope.description.length < 15 || formData.projectScope.description.length > 600) ? "border-red-500" : "border-gray-300"}`}
                        placeholder={t("lawyerForm.projectDescriptionPlaceholder")}
                        maxLength={600}
                    />
                    <div className="flex justify-between items-center mt-2">
                        <div className="flex items-center gap-2">
                            <span className={`text-sm ${formData.projectScope.description.length > 0 && (formData.projectScope.description.length < 15 || formData.projectScope.description.length > 600) ? "text-red-500" : "text-gray-400"}`}>
                                {formData.projectScope.description.length}/600
                            </span>
                            {formData.projectScope.description.length > 0 && formData.projectScope.description.length < 15 && (
                                <span className="text-xs text-red-500">{t("lawyerForm.descriptionMinLength")}</span>
                            )}
                            {formData.projectScope.description.length > 600 && (
                                <span className="text-xs text-red-500">{t("lawyerForm.descriptionMaxLength")}</span>
                            )}
                        </div>
                    </div>
                    <div className="mt-4 bg-yellow-50 border border-yellow-100 rounded-lg p-4">
                        <h3 className="font-medium text-yellow-800 mb-2">{t("lawyerForm.tipsForGreatDescription")}</h3>
                        <ul className="text-sm text-yellow-700 space-y-1">
                            <li>• {t("lawyerForm.specifyLegalAssistance")}</li>
                            <li>• {t("lawyerForm.mentionJurisdictions")}</li>
                            <li>• {t("lawyerForm.includeDeadlines")}</li>
                            <li>• {t("lawyerForm.listRequirements")}</li>
                            <li>• {t("lawyerForm.mentionLanguageReqs")}</li>
                        </ul>
                    </div>
                </Card>

                <Card className="p-6 overflow-visible">
                    <h2 className="text-xl font-semibold mb-4">{t("lawyerForm.budget")} <span className="text-red-500">{t("lawyerForm.required")}</span></h2>
                    <div className="space-y-6 overflow-visible">
                        <div>
                            <label className="block text-sm font-medium mb-2">{t("lawyerForm.budgetType")}</label>
                            <div className="grid grid-cols-2 gap-4">
                                <button
                                    onClick={() =>
                                        setFormData({
                                            ...formData,
                                            projectScope: {
                                                ...formData.projectScope,
                                                budget: { ...formData.projectScope.budget, type: "fixed" },
                                            },
                                        })
                                    }
                                    className={`p-4 rounded-lg border-2 transition-colors ${formData.projectScope.budget.type === "fixed"
                                        ? "border-blue-500 bg-blue-50"
                                        : "border-gray-200 hover:border-blue-200"
                                        }`}>
                                    <h3 className="font-medium">{t("lawyerForm.fixedPrice")}</h3>
                                    <p className="text-sm text-gray-500">{t("lawyerForm.fixedPriceDesc")}</p>
                                </button>
                                <button
                                    onClick={() =>
                                        setFormData({
                                            ...formData,
                                            projectScope: {
                                                ...formData.projectScope,
                                                budget: { ...formData.projectScope.budget, type: "hourly" },
                                            },
                                        })
                                    }
                                    className={`p-4 rounded-lg border-2 transition-colors ${formData.projectScope.budget.type === "hourly"
                                        ? "border-blue-500 bg-blue-50"
                                        : "border-gray-200 hover:border-blue-200"
                                        }`}>
                                    <h3 className="font-medium">{t("lawyerForm.hourlyRate")}</h3>
                                    <p className="text-sm text-gray-500">{t("lawyerForm.hourlyRateDesc")}</p>
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 overflow-visible">
                            <div>
                                <label className="block text-sm font-medium mb-2">{t("lawyerForm.amount")} <span className="text-red-500">*</span></label>
                                <div className="relative">
                                    <span className="absolute left-3 top-3 text-gray-500">
                                        {getCurrencySymbol(selectedCurrency?.code || t("lawyerForm.defaultCurrency"))}
                                    </span>
                                    <input
                                        type="number"
                                        value={formData.projectScope.budget.amount}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                projectScope: {
                                                    ...formData.projectScope,
                                                    budget: {
                                                        ...formData.projectScope.budget,
                                                        amount: e.target.value,
                                                    },
                                                },
                                            })
                                        }
                                        onBlur={() => handleFieldBlur("budgetAmount")}
                                        className={`w-full pl-8 pr-4 py-2 border rounded-lg ${errors.budgetAmount && (touched.budgetAmount || isSubmitted) ? "border-red-500" : "border-gray-300"}`}
                                        placeholder={
                                            formData.projectScope.budget.type === "hourly"
                                                ? t("lawyerForm.hourlyRate")
                                                : t("lawyerForm.totalBudget")
                                        }
                                        required
                                    />
                                </div>
                                {errors.budgetAmount && (touched.budgetAmount || isSubmitted) && (
                                    <p className="text-sm text-red-600 mt-1">{errors.budgetAmount}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">{t("lawyerForm.currency")} <span className="text-red-500">*</span></label>
                                <CurrencyDropdown
                                    selectedCurrency={selectedCurrency}
                                    onCurrencySelect={handleCurrencySelect}
                                    autoDetect={true}
                                    placeholder={t("lawyerForm.selectCurrency")}
                                />
                                {currencies.length === 0 && (
                                    <p className="text-sm text-gray-500 mt-1">{t("lawyerForm.loadingCurrencies")}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </Card>

                <Card className="p-6">
                    <h2 className="text-xl font-semibold mb-4">{t("lawyerForm.timeline")} <span className="text-red-500">{t("lawyerForm.required")}</span></h2>
                    <div>
                        <select
                            value={formData.projectScope.timeline}
                            onChange={e =>
                                setFormData({
                                    ...formData,
                                    projectScope: {
                                        ...formData.projectScope,
                                        timeline: e.target.value,
                                    },
                                })
                            }
                            className="w-full p-3 border rounded-lg">
                            <option value="">{t("lawyerForm.selectProjectDuration")}</option>
                            <option value="less_than_1_month">{t("lawyerForm.lessThan1Month")}</option>
                            <option value="1_to_3_months">{t("lawyerForm.oneToThreeMonths")}</option>
                            <option value="3_to_6_months">{t("lawyerForm.threeToSixMonths")}</option>
                            <option value="more_than_6_months">{t("lawyerForm.moreThan6Months")}</option>
                        </select>
                    </div>
                </Card>

                <Card className="p-6">
                    <h2 className="text-xl font-semibold mb-4">{t("lawyerForm.requiredExperience")} <span className="text-red-500">{t("lawyerForm.required")}</span></h2>
                    <select
                        value={formData.requirements.experience}
                        onChange={e =>
                            setFormData({
                                ...formData,
                                requirements: {
                                    ...formData.requirements,
                                    experience: e.target.value,
                                },
                            })
                        }
                        className="w-full p-3 border rounded-lg">
                        <option value="">{t("lawyerForm.selectExperienceLevel")}</option>
                        <option value="entry">{t("lawyerForm.entryLevel")}</option>
                        <option value="intermediate">{t("lawyerForm.intermediate")}</option>
                        <option value="expert">{t("lawyerForm.expert")}</option>
                        <option value="specialist">{t("lawyerForm.specialist")}</option>
                    </select>
                </Card>

                <Card className="p-6">
                    <h2 className="text-xl font-semibold mb-4">{t("lawyerForm.languageRequirements")} <span className="text-red-500">{t("lawyerForm.required")}</span></h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {languages.slice(0, languageDisplayLimit).map(lang => (
                            <label
                                key={lang.id}
                                className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${formData.requirements.languages.includes(lang.id)
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-200 hover:border-blue-200"
                                    }`}>
                                <input
                                    type="checkbox"
                                    checked={formData.requirements.languages.includes(lang.id)}
                                    onChange={e => {
                                        const langs = e.target.checked
                                            ? [...formData.requirements.languages, lang.id]
                                            : formData.requirements.languages.filter(
                                                id => id !== lang.id
                                            );
                                        setFormData({
                                            ...formData,
                                            requirements: {
                                                ...formData.requirements,
                                                languages: langs,
                                            },
                                        });
                                    }}
                                    className="sr-only"
                                />
                                <span className="mr-2 text-lg">{lang.initial}</span>
                                <span className="text-sm">{lang.name}</span>
                            </label>
                        ))}
                    </div>
                    
                    {/* Show More Button */}
                    {languages.length > languageDisplayLimit && languageDisplayLimit < languages.length && (
                        <div className="mt-4 text-center">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    const nextLimit = Math.min(languageDisplayLimit + 6, languages.length);
                                    setLanguageDisplayLimit(nextLimit);
                                }}
                                    className="px-6 py-2 text-sm"
                                >
                                    {t("lawyerForm.showMoreLanguages", { count: Math.min(6, languages.length - languageDisplayLimit) })}
                                </Button>
                        </div>
                    )}

                    {/* Show All Button when some are hidden */}
                    {languages.length > 6 && languageDisplayLimit < languages.length && (
                        <div className="mt-2 text-center">
                            <Button
                                variant="ghost"
                                onClick={() => setLanguageDisplayLimit(languages.length)}
                                    className="px-4 py-1 text-xs text-blue-600 hover:text-blue-800"
                                >
                                    {t("lawyerForm.showAllLanguages", { count: languages.length })}
                                </Button>
                        </div>
                    )}

                    {/* Collapse Button when all are shown */}
                    {languages.length > 6 && languageDisplayLimit === languages.length && (
                        <div className="mt-2 text-center">
                            <Button
                                variant="ghost"
                                onClick={() => setLanguageDisplayLimit(6)}
                                    className="px-4 py-1 text-xs text-gray-600 hover:text-gray-800"
                                >
                                    {t("lawyerForm.showLessLanguages")}
                                </Button>
                        </div>
                    )}

                    {languages.length === 0 && (
                        <p className="text-sm text-gray-500 mt-2">{t("lawyerForm.loadingLanguages")}</p>
                    )}
                </Card>

                <Card className="p-6">
                    <h2 className="text-xl font-semibold mb-4">{t("lawyerForm.additionalDocuments")}</h2>
                    <div className="space-y-4">
                        <input
                            type="file"
                            onChange={handleFileUpload}
                            multiple
                            accept="image/*,video/*"
                            className="hidden"
                            id="file-upload"
                        />
                        <label
                            htmlFor="file-upload"
                            className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                            {isUploading ? (
                                <>
                                    <span className="mt-2 text-sm text-gray-500">{t("lawyerForm.uploading")}</span>
                                    {uploadingFileName && (
                                        <span className="mt-1 text-xs text-gray-400">{uploadingFileName}</span>
                                    )}
                                </>
                            ) : (
                                <>
                                    <Upload className="h-8 w-8 text-gray-400 mb-2" />
                                    <span className="text-sm text-gray-500">{t("lawyerForm.clickToUpload")}</span>
                                </>
                            )}
                        </label>

                        {/* Upload Progress Bar - Only show when uploading */}
                        {isUploading && (
                            <div className="mt-4 space-y-2">
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-600">
                                        {uploadingFileName ? `${t("lawyerForm.uploading")} ${uploadingFileName}...` : t("lawyerForm.uploading")}
                                    </span>
                                    <span className="text-gray-500">{Math.round(uploadProgress)}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-3">
                                    <div
                                        className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                                        style={{ width: `${uploadProgress}%` }}
                                    ></div>
                                </div>
                            </div>
                        )}

                        {/* Display Uploaded Photos */}
                        {uploadedFiles.filter(f => {
                            const isImageType = f.type && f.type.startsWith("image/");
                            const isImageExtension = f.name && /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(f.name);
                            return isImageType || isImageExtension;
                        }).length > 0 && (
                                <div>
                                    <h3 className="text-lg font-medium mb-4">{t("lawyerForm.uploadedPhotos")}</h3>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                        {uploadedFiles
                                            .filter(f => {
                                                const isImageType = f.type && f.type.startsWith("image/");
                                                const isImageExtension = f.name && /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(f.name);
                                                return isImageType || isImageExtension;
                                            })
                                            .map((file) => (
                                                <div key={file.id} className="relative aspect-square">
                                                    <img
                                                        src={file.url}
                                                        alt={file.name}
                                                        className="w-full h-full object-cover rounded-lg"
                                                    />
                                                    <button
                                                        onClick={() => handleRemoveUploadedFile(file.id)}
                                                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600">
                                                        <X className="h-4 w-4" />
                                                    </button>
                                                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg">
                                                        {file.name}
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}

                        {/* Display Uploaded Videos */}
                        {uploadedFiles.filter(f => {
                            const isVideoType = f.type && f.type.startsWith("video/");
                            const isVideoExtension = f.name && /\.(mp4|webm|mov|avi|mkv|flv|wmv|m4v|3gp|ogv)$/i.test(f.name);
                            return isVideoType || isVideoExtension;
                        }).length > 0 && (
                                <div>
                                    <h3 className="text-lg font-medium mb-4">{t("lawyerForm.uploadedVideos")}</h3>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                        {uploadedFiles
                                            .filter(f => {
                                                const isVideoType = f.type && f.type.startsWith("video/");
                                                const isVideoExtension = f.name && /\.(mp4|webm|mov|avi|mkv|flv|wmv|m4v|3gp|ogv)$/i.test(f.name);
                                                return isVideoType || isVideoExtension;
                                            })
                                            .map((file) => (
                                                <div key={file.id} className="relative aspect-square">
                                                    <video
                                                        src={file.url}
                                                        className="w-full h-full object-cover rounded-lg"
                                                        controls
                                                        muted
                                                    />
                                                    <button
                                                        onClick={() => handleRemoveUploadedFile(file.id)}
                                                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600">
                                                        <X className="h-4 w-4" />
                                                    </button>
                                                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg">
                                                        {file.name}
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}

                        {/* Media Limits Info */}
                        <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                            <p><strong>{t("lawyerForm.mediaLimits")}</strong></p>
                            <p>• {t("lawyerForm.maxImages")}</p>
                            <p>• {t("lawyerForm.maxVideo")}</p>
                            <p>• {t("lawyerForm.currentMedia", { images: getCurrentImageCount(), videos: getCurrentVideoCount() })}</p>
                        </div>
                    </div>
                </Card>

                <Card className="p-6" style={{ textAlign: "left" }}>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold">{t("lawyerForm.contactInformation")}</h2>
                        {user && !isEditMode && (user.firstName || user.email || user.phone) && (
                            <span className="text-sm text-green-600 bg-green-50 px-2 py-1 rounded">
                                {t("lawyerForm.autoFilledFromProfile")}
                            </span>
                        )}
                    </div>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">{t("lawyerForm.fullName")} <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                value={formData.contactName}
                                onChange={e => handleFieldChange("contactName", e.target.value)}
                                onBlur={() => handleFieldBlur("contactName")}
                                className={`w-full px-4 py-2 border rounded-lg ${errors.contactName && (touched.contactName || isSubmitted) ? "border-red-500" : "border-gray-300"} ${user && user.firstName && user.lastName && !isEditMode ? "bg-green-50" : ""}`}
                                placeholder={t("lawyerForm.fullNamePlaceholder")}
                            />
                            {errors.contactName && (touched.contactName || isSubmitted) && (
                                <p className="text-sm text-red-600 mt-1">{errors.contactName}</p>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">{t("lawyerForm.email")} <span className="text-red-500">*</span></label>
                                <input
                                    type="email"
                                    value={formData.contactEmail}
                                    onChange={e => handleFieldChange("contactEmail", e.target.value)}
                                    onBlur={() => handleFieldBlur("contactEmail")}
                                    className={`w-full px-4 py-2 border rounded-lg ${errors.contactEmail && (touched.contactEmail || isSubmitted) ? "border-red-500" : "border-gray-300"} ${user && user.email && !isEditMode ? "bg-green-50" : ""}`}
                                    placeholder={t("lawyerForm.emailPlaceholder")}
                                />
                                {errors.contactEmail && (touched.contactEmail || isSubmitted) && (
                                    <p className="text-sm text-red-600 mt-1">{errors.contactEmail}</p>
                                )}
                                <p className="mt-1 text-sm text-gray-500">
                                    {t("lawyerForm.onlyVisibleAfterPurchase")}
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">{t("lawyerForm.phoneNumber")} <span className="text-red-500">*</span></label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-black-500 text-sm">+</span>
                                    </div>
                                    <input
                                        type="tel"
                                        value={formData.contactPhone.replace(/^\+/, "")}
                                        onChange={(e) => {
                                            let value = e.target.value.replace(/\D/g, "");
                                            if (value) {
                                                value = "+" + value;
                                            }
                                            handleFieldChange("contactPhone", value);
                                        }}
                                        placeholder={t("lawyerForm.phonePlaceholder")}
                                        className={`w-full pl-8 pr-3 py-2 border rounded-lg ${errors.contactPhone && (touched.contactPhone || isSubmitted) ? "border-red-500" : "border-gray-300"} ${user && user.phone && !isEditMode ? "bg-green-50" : ""}`}
                                    />
                                </div>
                                {errors.contactPhone && (touched.contactPhone || isSubmitted) && (
                                    <p className="text-sm text-red-600 mt-1">{errors.contactPhone}</p>
                                )}
                                <p className="mt-1 text-sm text-gray-500">
                                    {t("lawyerForm.onlyVisibleAfterPurchase")}
                                </p>
                            </div>
                        </div>

                        {/* WhatsApp Section */}
                        <div className="border-t pt-4">
                            <div className="flex items-center justify-between mb-4">
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={formData.canContactByWhatsapp}
                                        onChange={e => {
                                            setFormData(prev => ({
                                                ...prev,
                                                canContactByWhatsapp: e.target.checked,
                                                contactWhatsapp: e.target.checked ? prev.contactWhatsapp : "",
                                            }));
                                        }}
                                        className="h-4 w-4 text-blue-600 rounded"
                                    />
                                    <span>{t("lawyerForm.wantWhatsappContact")}</span>
                                </label>
                                {formData.canContactByWhatsapp && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            // Auto-fill with phone number if available
                                            setFormData(prev => ({
                                                ...prev,
                                                contactWhatsapp: prev.contactPhone,
                                            }));
                                        }}
                                        className="text-sm text-blue-600 hover:text-blue-700">
                                        {t("lawyerForm.usePhoneNumber")}
                                    </button>
                                )}
                            </div>

                            {formData.canContactByWhatsapp && (
                                <div className="space-y-2">
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <span className="text-black-500 text-sm">+</span>
                                        </div>
                                        <input
                                            type="tel"
                                            value={formData.contactWhatsapp.replace(/^\+/, "")}
                                            onChange={(e) => {
                                                let value = e.target.value.replace(/\D/g, "");
                                                if (value) {
                                                    value = "+" + value;
                                                }
                                                handleFieldChange("contactWhatsapp", value);
                                            }}
                                            placeholder={t("lawyerForm.whatsappPlaceholder")}
                                            className={`w-full pl-8 pr-3 py-2 border rounded-lg ${errors.contactWhatsapp && (touched.contactWhatsapp || isSubmitted) ? "border-red-500" : "border-gray-300"}`}
                                        />
                                    </div>
                                    {errors.contactWhatsapp && (touched.contactWhatsapp || isSubmitted) && (
                                        <p className="text-sm text-red-600 mt-1">{errors.contactWhatsapp}</p>
                                    )}
                                    <p className="text-sm text-gray-500">
                                        {t("lawyerForm.enterWhatsappWithCode")}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </Card>

                {/* Terms and Conditions Section */}
                <Card className="p-6">
                    <h2 className="text-xl font-semibold mb-4">{t("lawyerForm.termsAndConditions")} <span className="text-red-500">*</span></h2>
                    <div className="space-y-4">
                        <div className="flex items-start space-x-3">
                            <input
                                type="checkbox"
                                id="accept-terms"
                                checked={formData.acceptTerms}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    acceptTerms: e.target.checked
                                })}
                                className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                            />
                            <label htmlFor="accept-terms" className="text-sm text-gray-700">
                                {t("lawyerForm.agreeToTerms")}{" "}
                                <a 
                                    href="/terms-and-conditions" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {t("lawyerForm.termsOfService")}
                                </a>
                                {" "}{t("lawyerForm.and")}{" "}
                                <a 
                                    href="/privacy-policy" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {t("lawyerForm.privacyPolicy")}
                                </a>
                                . {t("lawyerForm.understandServiceTerms")}
                            </label>
                        </div>
                        
                        {!formData.acceptTerms && isSubmitted && (
                            <p className="text-sm text-red-600">
                                {t("lawyerForm.mustAcceptTerms")}
                            </p>
                        )}
                    </div>
                </Card>
            </div>
        );
    };
    const getCategoriesData = async () => {
        await dispatch(getTypeCategories({ id: TypeEnum.Home_Service }));
    };

    const getLanguagesData = async () => {
        await dispatch(getAllLanguages({
            page: 1,
            limit: 10,
            isShowLoader: false
        }));
    };

    const getCurrenciesData = async () => {
        await dispatch(getAllCurrencies({
            page: 1,
            limit: 10,
            isShowLoader: false
        }));
    };

    useEffect(() => {
        // Scroll to top when component mounts
        window.scrollTo({ top: 0, behavior: "smooth" });
        // Also try to scroll parent containers
        const scrollableParent = document.querySelector(".overflow-y-auto");
        if (scrollableParent) {
            scrollableParent.scrollTop = 0;
        }
        
        getCategoriesData();
        getLanguagesData();
        getCurrenciesData();
    }, []);

    // Auto-populate contact information from user data
    useEffect(() => {
        if (user && !isEditMode) {
            setFormData(prev => ({
                ...prev,
                contactName: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : prev.contactName,
                contactEmail: user.email || prev.contactEmail,
                contactPhone: user.phone || prev.contactPhone,
                contactWhatsapp: user.phone || prev.contactWhatsapp,
            }));
        }
    }, [user, isEditMode]);

    // Detect user location and set currency when currencies are loaded (RUN ONCE)
    useEffect(() => {
        if (currencies && currencies.length > 0 && !currencyDetectionRunRef.current) {
            currencyDetectionRunRef.current = true;
            
            // Normalize existing value if it's a code instead of backend id
            const currentId = formData.projectScope.budget.currency;
            const isKnownId = currencies.some((c: any) => c.value === currentId);
            if (!isKnownId && typeof currentId === "string") {
                const mappedId = utilFindCurrencyIdByCode(currentId, currencies);
                if (mappedId) {
                    setFormData(prev => ({
                        ...prev,
                        projectScope: {
                            ...prev.projectScope,
                            budget: {
                                ...prev.projectScope.budget,
                                currency: mappedId,
                            },
                        },
                    }));
                }
            }
            // Then try location detection to refine (only once)
            detectUserLocationAndSetCurrency();
        }
    }, [currencies]); // Remove formData dependency to prevent infinite loop

    // Initialize form data when used as component or in edit mode
    useEffect(() => {
        if (onSubmit && parentFormData) {
            // Initialize form data from parent component
            setFormData(prevData => ({
                ...prevData,
                requestType: parentFormData.requestType || "",
                // Add other mappings as needed
            }));
        }

        if (isEditMode && initialData) {
            // Initialize form data from existing job data
            setFormData(prevData => ({
                ...prevData,
                projectTitle: initialData.title || "",
                projectScope: {
                    ...prevData.projectScope,
                    description: initialData.description || "",
                    timeline: mapTimelineFromEnum(initialData.durationTimeline?.id),
                    budget: {
                        type: initialData.budgetType?.id === 1 ? "fixed" : "hourly",
                        amount: initialData.budget?.toString() || "",
                        currency: initialData.currency?.id || "",
                    },
                },
                requirements: {
                    ...prevData.requirements,
                    experience: mapExperienceFromEnum(initialData.requiredExperience?.id),
                    languages: initialData.languages?.map((lang: any) => lang.id) || [],
                    documents: initialData.files || [],
                },
                // Contact information
                contactName: initialData.contactName || "",
                contactEmail: initialData.contactEmail || "",
                contactPhone: initialData.contactPhone || "",
                canContactByWhatsapp: initialData.canContactByWhatsapp || false,
                contactWhatsapp: initialData.contactWhatsapp || "",
            }));

            // Initialize uploaded files from existing job data
            if (initialData.files && initialData.files.length > 0) {
                const existingFiles = initialData.files.map((file: any) => ({
                    id: file.id,
                    name: file.name || file.path?.split("/").pop() || `File ${file.id}`,
                    url: file.path || file.url || "",
                    type: file.type || "application/octet-stream"
                }));
                setUploadedFiles(existingFiles);
            }
        }
    }, [onSubmit, parentFormData, isEditMode, initialData]);

    // Helper functions to map enum IDs back to form values
    const mapTimelineFromEnum = (timelineId: number): string => {
        switch (timelineId) {
            case JobDurationTimelineEnum.Less_Than_One_Month:
                return "less_than_1_month";
            case JobDurationTimelineEnum.One_To_Three_Months:
                return "1_to_3_months";
            case JobDurationTimelineEnum.Three_To_Six_Months:
                return "3_to_6_months";
            case JobDurationTimelineEnum.More_Than_Six_Months:
                return "more_than_6_months";
            default:
                return "1_to_3_months";
        }
    };

    const mapExperienceFromEnum = (experienceId: number): string => {
        switch (experienceId) {
            case JobRequiredExperienceEnum.entry:
                return "entry";
            case JobRequiredExperienceEnum.intermediate:
                return "intermediate";
            case JobRequiredExperienceEnum.expert:
                return "expert";
            case JobRequiredExperienceEnum.specialist:
                return "specialist";
            default:
                return "intermediate";
        }
    };

    // Function to detect user location and set currency using centralized utility
    const detectUserLocationAndSetCurrency = async () => {
        if (!currencies || currencies.length === 0) {
            return;
        }

        try {
            // Use the centralized currency detection utility with Redux dispatch
            const result = await detectUserCurrency(currencies, dispatch);
            
            // Update form data with detected currency
            setFormData(prev => ({
                ...prev,
                projectScope: {
                    ...prev.projectScope,
                    budget: {
                        ...prev.projectScope.budget,
                        currency: result.currencyId || prev.projectScope.budget.currency,
                    },
                },
            }));
        } catch (error) {
            console.error("Error detecting currency:", error);
            // Fallback to USD
            const usdId = utilFindCurrencyIdByCode("USD", currencies);
            if (usdId) {
                setFormData(prev => ({
                    ...prev,
                    projectScope: {
                        ...prev.projectScope,
                        budget: {
                            ...prev.projectScope.budget,
                            currency: usdId,
                        },
                    },
                }));
            }
        }
    };

    // Helper functions to get accurate media counts
    const getCurrentImageCount = () => {
        return uploadedFiles.filter(f => {
            const isImageType = f.type && f.type.startsWith("image/");
            const isImageExtension = f.name && /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(f.name);
            return isImageType || isImageExtension;
        }).length;
    };

    const getCurrentVideoCount = () => {
        return uploadedFiles.filter(f => {
            const isVideoType = f.type && f.type.startsWith("video/");
            const isVideoExtension = f.name && /\.(mp4|webm|mov|avi|mkv|flv|wmv|m4v|3gp|ogv)$/i.test(f.name);
            return isVideoType || isVideoExtension;
        }).length;
    };

    // File upload handler
    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        // Separate files by type for processing using improved detection
        const imageFiles = Array.from(files).filter(f => {
            const isImageType = f.type && f.type.startsWith("image/");
            const isImageExtension = f.name && /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(f.name);
            return isImageType || isImageExtension;
        });
        const videoFiles = Array.from(files).filter(f => {
            const isVideoType = f.type && f.type.startsWith("video/");
            const isVideoExtension = f.name && /\.(mp4|webm|mov|avi|mkv|flv|wmv|m4v|3gp|ogv)$/i.test(f.name);
            return isVideoType || isVideoExtension;
        });

        // Check image limit (5 images max) - use helper function for accurate count
        const currentImageCount = getCurrentImageCount();
        if (currentImageCount + imageFiles.length > 5) {
            GlobalService.showToast("warning", "Image Limit Exceeded", `Maximum 5 images allowed. You currently have ${currentImageCount} images. Please remove some images first.`);
            return;
        }

        // Check video limit (1 video max) - use helper function for accurate count
        const currentVideoCount = getCurrentVideoCount();
        if (currentVideoCount + videoFiles.length > 1) {
            GlobalService.showToast("warning", "Video Limit Exceeded", "Maximum 1 video allowed. Please remove the existing video first.");
            return;
        }

        // Start progress immediately
        setIsUploading(true);
        setUploadProgress(5); // Start at 5% to show progress bar is active
        setUploadingFileName(t("lawyerForm.startingUpload"));

        try {
            const newUploadedFiles: Array<{ id: string, name: string, url: string, type: string }> = [];

            for (let i = 0; i < Array.from(files).length; i++) {
                const file = Array.from(files)[i];
                setUploadingFileName(file.name);

                // Realistic progress simulation for each file
                const progressInterval = setInterval(() => {
                    setUploadProgress(prev => {
                        const fileProgress = ((i + 1) / Array.from(files).length) * 100;
                        const targetProgress = Math.min(prev + 4, fileProgress - 15);
                        if (prev >= fileProgress - 15) {
                            clearInterval(progressInterval);
                            return prev;
                        }
                        return targetProgress;
                    });
                }, 180); // Realistic interval

                const { payload } = await dispatch(uploadContractFile(file) as any);
                const fileId = payload?.file?.id;
                const fileUrl = payload?.file?.path;

                if (fileId) {
                    newUploadedFiles.push({
                        id: fileId,
                        name: file.name,
                        url: fileUrl || URL.createObjectURL(file),
                        type: file.type,
                    });
                }

                // Update progress for this file
                const fileProgress = ((i + 1) / Array.from(files).length) * 100;
                setUploadProgress(fileProgress);
            }

            // Complete the progress and keep it visible briefly
            setUploadProgress(100);
            setUploadingFileName(t("lawyerForm.uploadCompleted"));

            setTimeout(() => {
                setUploadProgress(0);
                setUploadingFileName("");
                setIsUploading(false);
            }, 800); // Brief completion display

            setUploadedFiles(prev => [...prev, ...newUploadedFiles]);

            // Update the form data files array with file IDs
            setFormData(prev => ({
                ...prev,
                requirements: {
                    ...prev.requirements,
                    documents: [
                        ...prev.requirements.documents,
                        ...newUploadedFiles.map(file => ({ id: file.id }))
                    ],
                },
            }));

            GlobalService.showToast("success", t("toast.titles.success"), t("toast.upload.filesUploadedWithExclamation", { count: newUploadedFiles.length }));
        } catch (error) {
            console.error("Error uploading files:", error);
            GlobalService.showToast("error", "Upload Failed", "Failed to upload one or more files");
            setUploadProgress(0);
            setUploadingFileName("");
            setIsUploading(false);
        }
    };

    // Remove uploaded file
    const handleRemoveUploadedFile = (fileId: string) => {
        // Find the file to determine its type
        const fileToRemove = uploadedFiles.find(file => file.id === fileId);

        setUploadedFiles(prev => prev.filter(file => file.id !== fileId));

        // Remove from form data files array
        setFormData(prev => ({
            ...prev,
            requirements: {
                ...prev.requirements,
                documents: prev.requirements.documents.filter(file => file.id !== fileId),
            },
        }));

        // Log for debugging
        console.log(`Removed file: ${fileToRemove?.name} (${fileToRemove?.type})`);
        console.log(`Updated uploadedFiles count: ${uploadedFiles.length - 1}`);
    };

    return (
        <>
            {isLoading && (
                <div className="fixed left-[50%] top-[40%] z-[1000px]">
                    <Spinner color="green" />
                </div>
            )}

            {/* Only show header/footer when used standalone (not as component) */}
            {!onSubmit && (
                <div className={`min-h-screen bg-gray-50 ${isLoading ? "opacity-25" : "opacity-100"}`}>
                    <div className="max-w-4xl mx-auto pt-6">
                        <div className="mb-8 text-center" >
                            <h1 className="text-3xl font-bold text-gray-900">{t("lawyerForm.title")}</h1>
                            <p className="mt-2 text-gray-600">
                                {t("lawyerForm.subtitle")}
                            </p>
                        </div>
                    </div>

                    <main className="max-w-3xl mx-auto px-4 py-12">
                        {renderStep()}

                        <div className="flex justify-between mt-8">
                            <Button onClick={prevStep} variant="outline" disabled={currentStep === 1}>
                                {t("lawyerForm.previous")}
                            </Button>

                            {currentStep < 5 ? (
                                <Button onClick={nextStep} disabled={!isStepValid(currentStep)}>
                                    {t("lawyerForm.next")}
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleSubmit}
                                    disabled={!isStepValid(currentStep) || isUploading}>
                                    {isUploading ? t("lawyerForm.uploadingFiles") || "Uploading files..." : t("lawyerForm.postProject")}
                                </Button>
                            )}
                        </div>
                    </main>
                    <br></br>
                    <br></br>
                    <br></br>
                    <br></br>

                    <SharedFooter3 />
                </div>
            )}

            {/* Component mode - just render the form */}
            {onSubmit && (
                <div className="space-y-6 mb-24">
                    {renderStep()}

                    <div className="flex justify-between mt-8">
                        {onBack ? (
                            <Button onClick={onBack} variant="outline">
                                {t("lawyerForm.back")}
                            </Button>
                        ) : (
                            <Button onClick={prevStep} variant="outline" disabled={currentStep === 1}>
                                {t("lawyerForm.previous")}
                            </Button>
                        )}
                        <Button
                            onClick={handleSubmit}
                            disabled={!isStepValid(currentStep) || isUploading}>
                            {isUploading ? t("lawyerForm.uploadingFiles") || "Uploading files..." : t("lawyerForm.continueToHiring")}
                        </Button>
                    </div>
                </div>
            )}
        </>
    );
};

export default LawyerRequestForm;
